/**
 * useWikiGraph — drives the wiki side-panel from streamed tool events.
 *
 * Default UX: the panel does NOT auto-open. As the agent calls wiki tools we
 * record the notes it jumps to (built purely from the tool args — no full-tree
 * fetch); the panel auto-opens the first time a page is actually opened
 * the panel. When open, the panel shows ONLY the visited notes, in real time.
 *
 * The full wiki tree is kept but off by default (mode='trail'); switching to
 * mode='full' lazily fetches the whole graph.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@services/api';
import { wikiPageFolder, wikiPageKey, wikiPageName } from '@modules/wiki/utils/pageRef';

export interface WikiNote { id: string; label: string; folder: string }
export interface WikiEdge { source: string; target: string }

export function useWikiGraph(conversationId: string | null | undefined) {
  const [sequence, setSequence] = useState<string[]>([]);   // ordered note keys (consecutive-deduped)
  const metaRef = useRef<Map<string, WikiNote>>(new Map());
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const [mode, setMode] = useState<'trail' | 'full'>('trail');
  const [fullNodes, setFullNodes] = useState<WikiNote[]>([]);
  const [fullEdges, setFullEdges] = useState<WikiEdge[]>([]);
  const [fullLoading, setFullLoading] = useState(false);
  const fullFetched = useRef(false);
  const openedThisTurn = useRef(false);
  // Set at each send (turn boundary); the trail is only actually cleared when
  // the NEW turn's first wiki page is opened. This keeps the previous trail
  // visible through non-wiki turns (e.g. an artifact turn) instead of
  // blanking the panel into its "exploring…" empty state.
  const pendingResetRef = useRef(false);

  const onWikiTool = useCallback((tool?: string, args?: Record<string, unknown>, wikiPages?: unknown) => {
    if (!tool || !tool.startsWith('wiki_')) return;
    // We do NOT auto-open on discovery-only tools (grep/search/list_pages —
    // they open no page, so popping the panel would show an empty graph).
    // Prefer the exact page list the backend computed for this call — it covers a
    // BATCH read (wiki_read_pages), which opens several pages that never appear in
    // the singular input args. Fall back to the single page arg for one-page tools.
    let pages: string[] = [];
    if (Array.isArray(wikiPages)) {
      pages = wikiPages.filter((p): p is string => typeof p === 'string' && !!p.trim());
    }
    if (pages.length === 0) {
      const single = (args?.page ?? args?.path ?? args?.title) as unknown;
      if (typeof single === 'string' && single.trim()) pages = [single];
    }
    if (pages.length === 0) return;

    const keys: string[] = [];
    for (const page of pages) {
      // One node per page, labelled by its name alone (utils/pageRef).
      const key = wikiPageKey(page);
      if (!key) continue;
      if (!metaRef.current.has(key)) {
        metaRef.current.set(key, { id: key, label: wikiPageName(page), folder: wikiPageFolder(page) });
      }
      keys.push(key);
    }
    if (keys.length === 0) return;

    // First page of a NEW turn → now (and only now) drop the previous
    // turn's trail so this walk starts fresh.
    const startFresh = pendingResetRef.current;
    pendingResetRef.current = false;

    // A page was actually opened → auto-open the panel the first time this turn.
    if (!openedThisTurn.current) { openedThisTurn.current = true; setOpen(true); }

    // Append each newly-visited note in order, consecutive-deduped, so the trail
    // animates through every page the agent opened in this call.
    setSequence((prev) => {
      const next = startFresh ? [] : [...prev];
      let last = next[next.length - 1];
      for (const k of keys) {
        if (k !== last) { next.push(k); last = k; }
      }
      return next;
    });
    setCurrentId(keys[keys.length - 1]);
  }, []);

  const ensureFullGraph = useCallback(async () => {
    if (fullFetched.current || !conversationId) return;
    fullFetched.current = true;
    setFullLoading(true);
    try {
      const { data } = await api.get(`/wiki/conversation/${conversationId}/graph`);
      setFullNodes(data?.nodes || []);
      setFullEdges(data?.edges || []);
    } catch {
      /* no wiki bound / not ready — leave empty */
    } finally {
      setFullLoading(false);
    }
  }, [conversationId]);

  const changeMode = useCallback((m: 'trail' | 'full') => {
    setMode(m);
    if (m === 'full') void ensureFullGraph();
  }, [ensureFullGraph]);

  // Prefetch the graph as soon as the panel opens: the trail view needs the
  // real wikilink edges between visited notes (not just the jump order), and
  // it makes switching to "Full map" instant.
  useEffect(() => { if (open) void ensureFullGraph(); }, [open, ensureFullGraph]);

  // Unique visited notes, in the order first seen.
  const notes = useMemo(() => {
    const seen = new Set<string>();
    const out: WikiNote[] = [];
    for (const k of sequence) {
      if (seen.has(k)) continue;
      seen.add(k);
      const m = metaRef.current.get(k);
      if (m) out.push(m);
    }
    return out;
  }, [sequence]);

  // Mark a turn boundary. Deliberately LAZY: the visible trail survives until
  // the next turn actually opens a wiki page (see onWikiTool) — a turn that
  // never touches the wiki leaves the previous trail on screen instead of an
  // empty "exploring…" placeholder.
  const resetTurn = useCallback(() => {
    pendingResetRef.current = true;
    openedThisTurn.current = false;
  }, []);
  const openPanel = useCallback(() => setOpen(true), []);

  return {
    notes, sequence, currentId, open, mode,
    fullNodes, fullEdges, fullLoading,
    onWikiTool, resetTurn, openPanel,
    setMode: changeMode,
  };
}
